<template>
  <div class="panel discrepancia-panel">
    <h3>Discrepancia Web vs ComprasParaguay
      <span class="total-badge">{{ alertas.length }} productos</span>
    </h3>
    <p class="subtitulo">Competidores con precio más barato en su web que en ComprasParaguay</p>

    <div v-if="alertas.length > 0" class="tiendas-list">
      <div v-for="(items, tienda) in agrupadoPorTienda" :key="tienda" class="tienda-row" @click="abrirModal(tienda, items)">
        <div class="tienda-info">
          <span class="tienda-nombre">{{ formatStoreName(tienda) }}</span>
          <span class="tienda-count">{{ items.length }} productos — diff promedio {{ promedio(items) }}%</span>
        </div>
        <button class="ver-btn">Ver detalle →</button>
      </div>
    </div>
    <div v-else class="no-alerts-message">
      Sin discrepancias detectadas entre fuentes.
    </div>

    <!-- Modal -->
    <div v-if="modalVisible" class="modal-overlay" @click.self="cerrarModal">
      <div class="modal-content">
        <div class="modal-header">
          <h4>{{ formatStoreName(modalTienda) }} — {{ modalItems.length }} discrepancias</h4>
          <div class="modal-actions">
            <button @click="exportarExcel" class="export-btn"><PhChartBar :size="13" weight="bold" /> EXCEL</button>
            <button @click="exportarCsv" class="export-btn export-btn-csv"><PhDownload :size="13" weight="bold" /> CSV</button>
            <button @click="cerrarModal" class="close-btn"><PhX :size="14" weight="bold" /></button>
          </div>
        </div>
        <div class="modal-body">
          <table class="modal-table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Producto</th>
                <th>Precio Web</th>
                <th>Precio CP</th>
                <th>Diferencia</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in modalItemsOrdenados" :key="item.codigo">
                <td class="code-cell">[{{ item.codigo }}]</td>
                <td>{{ item.descripcion }}</td>
                <td class="price-web">
                  <a v-if="item.url_web && !item.url_web.includes('lista-txt')" :href="item.url_web" target="_blank" class="price-link">${{ item.precio_web }}</a>
                  <span v-else-if="!item.url_web || item.url_web.includes('lista-txt')">${{ item.precio_web }}</span>
                </td>
                <td class="price-cp">
                  <a v-if="item.url_cp" :href="item.url_cp" target="_blank" class="price-link cp">${{ item.precio_cp }}</a>
                  <span v-else>${{ item.precio_cp }}</span>
                </td>
                <td class="diff-cell">+{{ item.diferencia_pct }}%</td>
                <td><button v-if="esSuperadmin" class="corregir-small" @click="marcarCorreccion(item.codigo)"><PhWarning :size="13" weight="bold" /></button></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useCorreccion } from '../composables/useCorreccion.js';
import { exportarAExcel, exportarACsv } from '../composables/useExcelExport';
import { PhChartBar, PhDownload, PhX, PhWarning } from '@phosphor-icons/vue'

const { esSuperadmin, marcarCorreccion } = useCorreccion();
const props = defineProps({ alertas: Array });

const modalVisible = ref(false);
const modalTienda = ref('');
const modalItems = ref([]);

const formatStoreName = (name) => {
  if (!name) return '';
  return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const promedio = (items) => {
  const avg = items.reduce((s, i) => s + i.diferencia_pct, 0) / items.length;
  return Math.round(avg * 10) / 10;
};

const agrupadoPorTienda = computed(() => {
  const grupos = {};
  for (const a of props.alertas) {
    if (!grupos[a.tienda]) grupos[a.tienda] = [];
    grupos[a.tienda].push(a);
  }
  return Object.fromEntries(
    Object.entries(grupos).sort((a, b) => b[1].length - a[1].length)
  );
});

const modalItemsOrdenados = computed(() =>
  [...modalItems.value].sort((a, b) => b.diferencia_pct - a.diferencia_pct)
);

const abrirModal = (tienda, items) => {
  modalTienda.value = tienda;
  modalItems.value = items;
  modalVisible.value = true;
};

const cerrarModal = () => { modalVisible.value = false; };

const _buildRowsDis = () => {
  const headers = ['Código', 'Producto', 'Precio Web', 'Precio CP', 'Diferencia %', 'Tienda'];
  const rows = modalItemsOrdenados.value.map(item => [
    item.codigo, item.descripcion,
    item.precio_web, item.precio_cp,
    item.diferencia_pct + '%',
    formatStoreName(modalTienda.value)
  ]);
  return { headers, rows };
};

const exportarExcel = () => {
  const { headers, rows } = _buildRowsDis();
  exportarAExcel({
    headers, rows,
    priceColumns: [2, 3],
    filename: `discrepancia_${modalTienda.value}_${new Date().toISOString().slice(0,10)}.xlsx`,
    sheetName: 'Discrepancia Fuentes'
  });
};

const exportarCsv = () => {
  const { headers, rows } = _buildRowsDis();
  exportarACsv({
    headers, rows,
    filename: `discrepancia_${modalTienda.value}_${new Date().toISOString().slice(0,10)}.csv`
  });
};
</script>

<style scoped>
/* ==================== HEADER ==================== */
.subtitulo {
  font-size: 11.5px;
  color: var(--color-text-muted);
  margin: -4px 0 14px;
  font-family: 'Inter', sans-serif;
}
.total-badge {
  font-family: var(--font-mono);
  font-size: 10.5px;
  background: var(--color-surface-2);
  border: 1px solid var(--color-border-strong);
  color: var(--color-text-muted);
  border-radius: 3px;
  padding: 2px 8px;
  margin-left: 10px;
  font-weight: 600;
  letter-spacing: 0.05em;
}

/* ==================== LISTA DE TIENDAS ==================== */
.tiendas-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
}
.tienda-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: var(--color-surface-2);
  border-left: 3px solid var(--color-leader);
  border-top: 1px solid var(--color-border);
  border-right: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
  border-radius: 2px;
  cursor: pointer;
  transition: all 0.15s;
}
.tienda-row:hover {
  background: var(--color-hover-row);
  border-color: var(--color-border-strong);
  transform: translateX(2px);
}
.tienda-nombre {
  font-weight: 600;
  font-size: 13px;
  color: var(--color-text);
  display: block;
}
.tienda-count {
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--color-text-muted);
  font-variant-numeric: tabular-nums;
}
.ver-btn {
  background: var(--color-accent-bg);
  color: var(--color-accent);
  border: 1px solid var(--color-accent-border);
  padding: 5px 12px;
  border-radius: 2px;
  cursor: pointer;
  font-size: 10.5px;
  font-family: var(--font-mono);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transition: all 0.15s;
}
.ver-btn:hover {
  background: var(--color-accent);
  color: var(--color-surface-3);
}
.no-alerts-message {
  padding: 16px;
  text-align: center;
  color: var(--color-text-muted);
  font-size: 12px;
  font-style: italic;
  background: var(--color-surface-2);
  border: 1px dashed var(--color-border-strong);
  border-radius: 2px;
}

/* ==================== MODAL ==================== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal-content {
  background: var(--color-surface);
  border: 1px solid var(--color-accent-border);
  border-radius: 3px;
  width: 92%;
  max-width: 900px;
  max-height: 82vh;
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadow-lg);
  overflow: hidden;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background: var(--color-surface-3);
  border-bottom: 1px solid var(--color-accent-border);
}
.modal-header h4 {
  margin: 0;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--color-accent);
  text-transform: uppercase;
}
.modal-actions { display: flex; gap: 6px; }
.export-btn {
  background: transparent;
  color: var(--color-text-muted);
  border: 1px solid var(--color-border-strong);
  padding: 4px 10px;
  border-radius: 2px;
  cursor: pointer;
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  transition: all 0.15s;
}
.export-btn:hover {
  color: var(--color-success);
  border-color: var(--color-success);
}
.export-btn-csv:hover {
  color: var(--color-text);
  border-color: var(--color-text-muted);
}
.close-btn {
  background: transparent;
  color: var(--color-text-muted);
  border: 1px solid var(--color-border-strong);
  padding: 4px 8px;
  border-radius: 2px;
  cursor: pointer;
  font-size: 11px;
  font-family: var(--font-mono);
  transition: all 0.15s;
}
.close-btn:hover {
  color: var(--color-danger);
  border-color: var(--color-danger);
}
.modal-body {
  overflow-y: auto;
  padding: 12px 16px 16px;
}
.modal-table {
  width: 100%;
  border-collapse: collapse;
}
.modal-table th {
  text-align: left;
  padding: 6px 8px;
  font-family: var(--font-mono);
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-text-faint);
  border-bottom: 1px solid var(--color-border-strong);
  background: var(--color-surface-3);
  position: sticky;
  top: 0;
}
.modal-table td {
  padding: 7px 8px;
  font-size: 11.5px;
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text);
  font-variant-numeric: tabular-nums;
}
.modal-table tbody tr:hover { background: var(--color-hover-row); }
.code-cell {
  font-family: var(--font-mono);
  color: var(--color-text-faint);
  font-size: 10.5px;
}
.price-web, .price-link {
  color: var(--color-success-text);
  font-weight: 600;
  font-family: var(--font-mono);
  text-decoration: none;
}
.price-link:hover { text-decoration: underline; }
.price-cp, .price-link.cp {
  color: var(--color-warning-text);
  font-weight: 600;
  font-family: var(--font-mono);
  text-decoration: none;
}
.price-link.cp:hover { text-decoration: underline; }
.diff-cell {
  color: var(--color-danger-text);
  font-weight: 700;
  font-family: var(--font-mono);
}
.corregir-small {
  background: transparent;
  color: var(--color-warning);
  border: 1px solid var(--color-warning-border);
  padding: 2px 6px;
  border-radius: 2px;
  cursor: pointer;
  font-size: 10px;
  margin-left: 4px;
  transition: all 0.1s;
}
.corregir-small:hover {
  background: var(--color-warning-bg);
}
</style>